import { z } from 'zod';
import { IsoDateTime, LanguageCodeSchema, RoleSchema } from './common.js';

export const AppointmentTypeSchema = z.object({
  code: z.string().min(1).describe('Stable code, e.g. NEW, FOLLOWUP, PROCEDURE'),
  display: z.string(),
  displayAr: z.string().optional(),
  durationMinutes: z.number().int().min(5).max(480),
  /** Roles that may book this type via self-service. Empty means staff only. */
  patientBookable: z.boolean().default(false),
});
export type AppointmentType = z.infer<typeof AppointmentTypeSchema>;

export const VoiceSettingsSchema = z.object({
  defaultLanguage: LanguageCodeSchema.default('en-US'),
  /** When true the Transcribe stream uses automatic language identification among allowedLanguages. */
  autoLanguageIdentification: z.boolean().default(false),
  allowedLanguages: z.array(LanguageCodeSchema).default(['en-US', 'ar-AE']),
  silenceThresholdMs: z.number().int().min(300).max(5000).default(1200),
  maxCommandSeconds: z.number().int().min(5).max(120).default(30),
  shortcut: z.string().default('Control+Shift+Space'),
  pushToTalkShortcut: z.string().default('Control+Shift+Period'),
  arabicVoiceId: z.enum(['Hala', 'Zayd']).default('Hala'),
  englishVoiceId: z.string().default('Joanna'),
});
export type VoiceSettings = z.infer<typeof VoiceSettingsSchema>;

/** Specialty configuration keeps the product general-purpose: clinics customise by config, not by code. */
export const SpecialtyConfigSchema = z.object({
  code: z.string().default('GENERAL'),
  display: z.string().default('General outpatient'),
  /** Lab panels offered in quick order, by LOINC/local code. */
  favoriteLabCodes: z.array(z.string()).default([]),
  favoriteImagingCodes: z.array(z.string()).default([]),
  /** CDS rule packs enabled for this tenant (ids from the CDS catalog). */
  enabledCdsRulePacks: z.array(z.string()).default(['core-safety', 'preventive-screening']),
  /** Questionnaire ids shown at registration or intake. */
  intakeQuestionnaireIds: z.array(z.string()).default([]),
});
export type SpecialtyConfig = z.infer<typeof SpecialtyConfigSchema>;

export const TenantSettingsSchema = z.object({
  timeZone: z.string().default('Asia/Riyadh'),
  defaultLocale: LanguageCodeSchema.default('en-US'),
  appointmentTypes: z.array(AppointmentTypeSchema).default([]),
  workingDays: z.array(z.number().int().min(0).max(6)).default([0, 1, 2, 3, 4]),
  voice: VoiceSettingsSchema.default({}),
  specialty: SpecialtyConfigSchema.default({}),
  /** Hours after creation that a pending (unconfirmed) action remains valid. */
  pendingActionTtlMinutes: z.number().int().min(1).max(1440).default(30),
  mrnPrefix: z.string().default(''),
});
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;

export const TenantStatusSchema = z.enum(['ACTIVE', 'SUSPENDED', 'TRIAL', 'CLOSED']);

export const TenantSchema = z.object({
  tenantId: z.string().min(1),
  name: z.string().min(1),
  nameAr: z.string().optional(),
  status: TenantStatusSchema.default('TRIAL'),
  plan: z.enum(['TRIAL', 'STARTER', 'CLINIC', 'ENTERPRISE']).default('TRIAL'),
  settings: TenantSettingsSchema.default({}),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type Tenant = z.infer<typeof TenantSchema>;

export const UserPreferencesSchema = z.object({
  voiceOutputEnabled: z.boolean().default(false),
  preferredVoiceId: z.string().optional(),
  preferredLanguage: LanguageCodeSchema.optional(),
  listeningMode: z.enum(['PRESS_TO_TOGGLE', 'PRESS_AND_HOLD']).default('PRESS_TO_TOGGLE'),
  shortcut: z.string().optional(),
  silenceThresholdMs: z.number().int().min(300).max(5000).optional(),
});
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;

export const UserSchema = z.object({
  tenantId: z.string().min(1),
  userId: z.string().min(1),
  /** Cognito `sub`. */
  identitySub: z.string().optional(),
  email: z.string().email(),
  displayName: z.string().min(1),
  displayNameAr: z.string().optional(),
  roles: z.array(RoleSchema).min(1),
  /** For PATIENT users: linked patient record. */
  patientId: z.string().optional(),
  /** For clinicians: provider profile used by scheduling. */
  providerId: z.string().optional(),
  status: z.enum(['ACTIVE', 'DISABLED', 'INVITED']).default('ACTIVE'),
  preferences: UserPreferencesSchema.default({}),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type User = z.infer<typeof UserSchema>;

export const ProviderSchema = z.object({
  tenantId: z.string(),
  providerId: z.string(),
  userId: z.string().optional(),
  displayName: z.string(),
  displayNameAr: z.string().optional(),
  specialty: z.string().optional(),
  active: z.boolean().default(true),
});
export type Provider = z.infer<typeof ProviderSchema>;
